import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {AppConfigurationProcessTaskSchemaSelectorQuery} from './__generated/AppConfigurationProcessTaskSchemaSelectorQuery';

type ProcessTaskSchemas =
  AppConfigurationProcessTaskSchemaSelectorQuery['processTaskSchemasSupportingPlatformFeature'][number];

export const APP_CONFIGURATION_PROCESS_TASK_SCHEMA_SELECTOR_QUERY = gql`
  query AppConfigurationProcessTaskSchemaSelectorQuery @skipAudit {
    processTaskSchemasSupportingPlatformFeature(platformFeature: WORKFLOWS_SEARCH) {
      name
      id
    }
  }
`;
interface BaseProps {
  processTaskSchemas: Array<ProcessTaskSchemas>;
  disabled: boolean;
  selectedProcessTaskSchemaId: string | null;
  onChange: (newSelectedSchemaId: string | null) => void;
}

export function AppConfigurationProcessTaskSchemaSelector(props: BaseProps): JSX.Element {
  const optionDatas = [
    {
      name: 'No Workflow Task Schema',
      value: null,
    },
    {
      name: 'Workflow Task Schemas',
      isHeader: true,
    },
    ...props.processTaskSchemas.map(({id, name}) => ({
      name,
      value: id,
    })),
  ];
  const selectedProcessTaskSchemaDisplayName = props.processTaskSchemas.find(
    (taskSchema) => taskSchema.id === props.selectedProcessTaskSchemaId
  )?.name;

  return (
    <SelectDropdown
      menu={<SearchableMenu width={Menu.WIDTHS.FULL} optionDatas={optionDatas} onSelect={props.onChange} />}
      displayName={selectedProcessTaskSchemaDisplayName || 'Select a Workflow Task Schema'}
      disabled={props.disabled}
    />
  );
}

export default function QueryingAppConfigurationProcessTaskSchemaSelector(
  props: Omit<BaseProps, 'processTaskSchemas'>
): JSX.Element {
  const result = useBenchlingQuery<AppConfigurationProcessTaskSchemaSelectorQuery>(
    APP_CONFIGURATION_PROCESS_TASK_SCHEMA_SELECTOR_QUERY
  );
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Unable to query workflow task schemas. Try refreshing the page.');
    return <div />;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber suppressPadding={true} />;
  } else {
    return (
      <AppConfigurationProcessTaskSchemaSelector
        {...props}
        processTaskSchemas={result.data.processTaskSchemasSupportingPlatformFeature}
      />
    );
  }
}
